import { useCallback, useState } from "react";
import { ShopAppEvents } from "../app-shop/events";
import { IBeginShoppingRequest } from "../entities-generated/models/project/begin-shopping-request";
import { IProjectConfiguration, ProjectConfiguration } from "../entities-generated/models/project/project-configuration";
import { EventNotificationService, useEventNotification } from "../framework/services/event-notification";
import { LocalStorageService } from "../framework/services/local-storage";
import { isNotNullOrWhitespace } from "../framework/utils";
import { ShopAuthApiClient } from "./shop-auth-client";

const CUSTOMER_ACCT_KEY = 'Bwj.Storm.CustomerAccount';

class CustomerAccountServiceClass {
  public isAuthenticated() {
    return this.getAccountToken() !== null;
  }

  public getAccountToken() {
    if(!!this.accountToken) { return this.accountToken; }
    
    this.accountToken = LocalStorageService.get<string>(CUSTOMER_ACCT_KEY) ?? null;
    return this.accountToken;
  }

  public unsafeSetAccountTokenHeyAreYouSureYouKnowWhatYouAreDoing(token: string) {
    this.accountToken = token;
    LocalStorageService.set(CUSTOMER_ACCT_KEY, this.accountToken as string);;
  }

  public signIn(request: IBeginShoppingRequest) {
    this.clearAccountInfo();

    return ShopAuthApiClient.verifyShoppingCredentials(request)
      .then(resp => {
        if(isNotNullOrWhitespace(resp.signOn)) {
          this.accountToken = resp.signOn;
          this.projectConfiguration = resp.project;
          LocalStorageService.set(CUSTOMER_ACCT_KEY, this.accountToken as string);
          EventNotificationService.dispatch(ShopAppEvents.ProjectConfigDidUpdate, resp.project);

          return { success: true };
        } 

        return { success: false };
      });
  }

  public getProjectConfiguration() {
    if (!!this.projectConfiguration) { return Promise.resolve(this.projectConfiguration); }
    const token  = this.getAccountToken();
    if(!token) {
      this.signOut();
      return Promise.resolve(null);
    }

    EventNotificationService.dispatch(ShopAppEvents.ShowAppLoadingWithMessage, 'Loading your catalog...');
    
    return ShopAuthApiClient.getProjectConfiguration(token)
    .then(resp => {
      this.projectConfiguration = resp;
      EventNotificationService.dispatch(ShopAppEvents.ShowAppReady);
      EventNotificationService.dispatch(ShopAppEvents.ProjectConfigDidUpdate, resp);
      return resp;
    })
    .catch(() => {
      EventNotificationService.dispatch(ShopAppEvents.ShowAppReady);
      this.signOut();
      return Promise.resolve(null);
    });
  }

  public getCachedProjectConfiguration() {
    return this.projectConfiguration;
  }

  public updateProjectConfiguration(config: IProjectConfiguration) {
    this.projectConfiguration = config;
    EventNotificationService.dispatch(ShopAppEvents.ProjectConfigDidUpdate, config);
  }

  public signOut() {
    this.clearAccountInfo();
    EventNotificationService.dispatch(ShopAppEvents.ProjectConfigDidUpdate, new ProjectConfiguration());
    EventNotificationService.dispatch(ShopAppEvents.ForceSignIn);
  }

  private clearAccountInfo() {
    LocalStorageService.clear(CUSTOMER_ACCT_KEY);
    this.accountToken = null;
    this.projectConfiguration = null;
  }

  // nothing besides the hook below should ever call this directly
  public projectConfiguration: IProjectConfiguration | null = null;
  private accountToken: string | null = null;
}

export const CustomerAccountService = new CustomerAccountServiceClass();

export const useProjectConfiguration = (subscriberId: string): IProjectConfiguration => {
    const [getter, setter] = useState<IProjectConfiguration>(CustomerAccountService.projectConfiguration ?? new ProjectConfiguration());

    const onConfigChange = useCallback((config: IProjectConfiguration) => setter(config), []);
    useEventNotification(onConfigChange, [onConfigChange], ShopAppEvents.ProjectConfigDidUpdate, subscriberId);

    return getter;
}